/*
----------------------------------------------------------------------------
AI challenges: area tabs, challenge filters, resource grid and dialogs
----------------------------------------------------------------------------
Usage:
import AiChallenges from './modules/ai-challenges.js'
_('ai-challenges').nodes().forEach((el) => new AiChallenges(el))

All resources for the library are rendered server-side, so filtering and
paging work across the full set without further requests. State is kept in
the query string (?area=&challenge=) so a view can be shared and restored.
*/

import { _ } from '../utils.js'

const WISTIA_SCRIPT = 'https://fast.wistia.com/assets/external/E-v1.js'

export default class AiChallenges {
    constructor(el) {
        this.el = el
        this.initial = parseInt(el.dataset.initial, 10) || 9
        this.pageSize = parseInt(el.dataset.pageSize, 10) || 9
        this.tabs = _('ai-tab', 'element').nodesFrom(el)
        this.areas = _('ai-area', 'element').nodesFrom(el)
        this.panel = _('ai-panel', 'element').nodeFrom(el)
        this.cards = _('ai-card', 'element').nodesFrom(el)
        this.empty = _('ai-empty', 'element').nodeFrom(el)
        this.moreWrap = _('ai-more-wrap', 'element').nodeFrom(el)
        this.more = _('ai-more', 'element').nodeFrom(el)
        this.status = _('ai-status', 'element').nodeFrom(el)
        this.dialog = _('ai-dialog', 'element').nodeFrom(el)
        this.dialogBody = _('ai-dialog-body', 'element').nodeFrom(el)
        this.opener = null

        const params = new URLSearchParams(window.location.search)
        const areaFromUrl = params.get('area')
        this.area = this.tabs.some((tab) => tab.dataset.area === areaFromUrl) ? areaFromUrl : (this.tabs[0] && this.tabs[0].dataset.area) || ''
        this.challenge = params.get('challenge') || ''
        this.visible = this.initial

        this.bindTabs()
        this.bindFilters()
        this.bindDialog()

        this.more.addEventListener('click', this.loadMore)
        _('ai-reset-filter', 'element').nodeFrom(el).addEventListener('click', () => this.selectChallenge(''))

        this.apply({ announce: false })
    }

    bindTabs = () => {
        this.tabs.forEach((tab, index) => {
            tab.addEventListener('click', () => this.selectArea(tab.dataset.area))
            tab.addEventListener('keydown', (event) => {
                const keys = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: this.tabs.length - 1 }
                if (!(event.key in keys)) {
                    return
                }
                event.preventDefault()
                const next = this.tabs[(keys[event.key] + this.tabs.length) % this.tabs.length]
                next.focus()
                this.selectArea(next.dataset.area)
            })
        })
    }

    bindFilters = () => {
        _('ai-filter', 'element').nodesFrom(this.el).forEach((button) => {
            button.addEventListener('click', () => this.selectChallenge(button.dataset.challenge))
        })
    }

    selectArea = (area) => {
        if (area === this.area) {
            return
        }
        this.area = area
        this.challenge = ''
        this.visible = this.initial
        this.apply()
    }

    selectChallenge = (challenge) => {
        this.challenge = challenge
        this.visible = this.initial
        this.apply()
    }

    matches = (card) => {
        const areas = card.dataset.areas ? card.dataset.areas.split(',') : []
        const challenges = card.dataset.challenges ? card.dataset.challenges.split(',') : []
        if (this.area && !areas.includes(this.area)) {
            return false
        }
        return !this.challenge || challenges.includes(this.challenge)
    }

    apply = ({ announce = true, focusFrom = null } = {}) => {
        const activeArea = this.areas.find((area) => area.dataset.area === this.area)
        if (this.challenge && activeArea && !_('ai-filter', 'element').nodesFrom(activeArea).some((b) => b.dataset.challenge === this.challenge)) {
            this.challenge = ''
        }

        this.tabs.forEach((tab) => {
            const selected = tab.dataset.area === this.area
            tab.setAttribute('aria-selected', selected ? 'true' : 'false')
            tab.tabIndex = selected ? 0 : -1
            if (selected) {
                this.panel.setAttribute('aria-labelledby', tab.id)
            }
        })
        this.areas.forEach((area) => {
            area.hidden = area.dataset.area !== this.area
            _('ai-filter', 'element').nodesFrom(area).forEach((button) => {
                button.setAttribute('aria-pressed', button.dataset.challenge === this.challenge ? 'true' : 'false')
            })
        })

        const matching = this.cards.filter(this.matches)
        this.cards.forEach((card) => {
            card.hidden = true
        })
        matching.slice(0, this.visible).forEach((card) => {
            card.hidden = false
        })

        this.empty.hidden = matching.length > 0 || this.cards.length === 0
        this.moreWrap.hidden = matching.length <= this.visible
        this.more.disabled = false

        this.updateUrl()

        if (focusFrom !== null && matching[focusFrom]) {
            const target = matching[focusFrom].querySelector('.ai-card__action, .ai-card__title')
            if (target) {
                if (!target.matches('button, a')) {
                    target.tabIndex = -1
                }
                target.focus()
            }
        }

        if (announce) {
            const shown = Math.min(this.visible, matching.length)
            this.status.textContent = matching.length
                ? `Showing ${shown} of ${matching.length} resources.`
                : 'No resources match this challenge.'
        }
    }

    loadMore = () => {
        this.more.disabled = true
        this.more.setAttribute('aria-busy', 'true')
        const from = this.visible
        this.visible += this.pageSize
        window.requestAnimationFrame(() => {
            this.more.removeAttribute('aria-busy')
            this.apply({ focusFrom: from })
        })
    }

    updateUrl = () => {
        const url = new URL(window.location.href)
        const firstArea = this.tabs[0] && this.tabs[0].dataset.area
        if (this.area && this.area !== firstArea) {
            url.searchParams.set('area', this.area)
        } else {
            url.searchParams.delete('area')
        }
        if (this.challenge) {
            url.searchParams.set('challenge', this.challenge)
        } else {
            url.searchParams.delete('challenge')
        }
        window.history.replaceState(window.history.state, '', url)
    }

    // ---------------------------------------------------------------- dialog

    bindDialog = () => {
        if (!this.dialog) {
            return
        }
        _('ai-open-modal', 'element').nodesFrom(this.el).forEach((button) => {
            button.addEventListener('click', () => {
                const template = document.getElementById(button.dataset.template)
                if (template) {
                    this.openDialog(button, template.content.cloneNode(true), 'modal')
                }
            })
        })
        _('ai-open-video', 'element').nodesFrom(this.el).forEach((button) => {
            button.addEventListener('click', () => this.openDialog(button, this.videoContent(button), 'video'))
        })

        _('ai-dialog-close', 'element').nodeFrom(this.dialog).addEventListener('click', this.closeDialog)
        this.dialog.addEventListener('click', (event) => {
            // Clicks on the ::backdrop target the dialog element itself.
            if (event.target === this.dialog) {
                this.closeDialog()
            }
        })
        this.dialog.addEventListener('close', this.onDialogClosed)
        this.dialog.addEventListener('cancel', () => this.closeDialog())
    }

    videoContent = (button) => {
        const fragment = document.createDocumentFragment()
        const title = document.createElement('h2')
        title.className = 'ai-modal__title sr-only'
        title.id = this.dialog.getAttribute('aria-labelledby')
        title.textContent = button.dataset.title
        const frame = document.createElement('div')
        frame.className = 'ai-dialog__video'

        if (button.dataset.provider === 'wistia') {
            const embed = document.createElement('div')
            embed.className = `wistia_embed wistia_async_${button.dataset.videoId.replace(/[^A-Za-z0-9_-]/g, '')} autoPlay=true videoFoam=true`
            frame.appendChild(embed)
            this.loadWistia()
        } else if (button.dataset.embed) {
            const iframe = document.createElement('iframe')
            iframe.src = button.dataset.embed
            iframe.title = button.dataset.title
            // The site sends Referrer-Policy: same-origin; YouTube refuses embeds without a referrer (error 153).
            iframe.referrerPolicy = 'strict-origin-when-cross-origin'
            iframe.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media'
            iframe.allowFullscreen = true
            frame.appendChild(iframe)
        }

        fragment.append(title, frame)
        return fragment
    }

    loadWistia = () => {
        if (document.querySelector(`script[src="${WISTIA_SCRIPT}"]`)) {
            return
        }
        const script = document.createElement('script')
        script.src = WISTIA_SCRIPT
        script.async = true
        document.head.appendChild(script)
    }

    openDialog = (opener, content, type) => {
        this.opener = opener
        this.dialog.classList.toggle('ai-dialog--video', type === 'video')
        this.dialogBody.replaceChildren(content)
        this.dialogBody.scrollTop = 0
        document.documentElement.classList.add('has-ai-dialog')
        this.dialog.showModal()
        const close = _('ai-dialog-close', 'element').nodeFrom(this.dialog)
        close.focus()
    }

    closeDialog = () => {
        if (this.dialog.open) {
            this.dialog.close()
        }
    }

    onDialogClosed = () => {
        // Clearing the body also stops any playing video.
        this.dialogBody.replaceChildren()
        document.documentElement.classList.remove('has-ai-dialog')
        if (this.opener) {
            this.opener.focus()
            this.opener = null
        }
    }
}
